import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { IModelExtended } from "@/components/Form/ModelSelectField";
import { ListFilter, X } from "lucide-react";
import {
  DonutIcon1,
  DonutIcon2,
  DonutIcon3,
  DonutIcon4,
  DonutIcon5,
} from "@/components/Icons";
import { Checkbox } from "@/components/shadcn/checkbox";
import { Button } from "@/components/shadcn/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";

// Buckets mirror the donut-icon breakpoints used in ModelPickerTable
// (limits [60, 70, 78, 85]) so the gauge shown next to each filter
// option is exactly the gauge users will see on matching rows.
const GAUGE_BUCKETS: {
  value: string;
  label: string;
  icon: React.ComponentType<{ fontSize?: string }>;
}[] = [
  { value: "0-60", label: "Very Low", icon: DonutIcon5 },
  { value: "60-70", label: "Low", icon: DonutIcon4 },
  { value: "70-78", label: "Medium", icon: DonutIcon3 },
  { value: "78-85", label: "High", icon: DonutIcon2 },
  { value: "85-100", label: "Excellent", icon: DonutIcon1 },
];

const getArrayFromProperty = (
  modelArray: IModelExtended[],
  property: string
) => {
  return modelArray.reduce(
    (acc: { label: string; value: string; iconUrl?: string }[], model) => {
      const newValue = model[property as keyof IModelExtended] as string;
      // Provider rows carry a logo URL on each model — surface it so
      // filter options can render the same icon shown next to the
      // provider in the table.
      const iconUrl =
        property === "provider"
          ? ((model as { provider_image?: string }).provider_image ?? undefined)
          : undefined;

      if (!acc.some((item) => item.label === newValue)) {
        acc.push({
          value: newValue,
          label: newValue,
          ...(iconUrl ? { iconUrl } : {}),
        });
      }

      return acc;
    },
    []
  );
};

const CAPABILITY_OPTIONS = [
  { value: "vision", label: "Vision" },
  { value: "function_calling", label: "Function Calling" },
  { value: "parallel_function_calling", label: "Parallel Function Calling" },
  { value: "streaming", label: "Streaming" },
  { value: "system_prompt", label: "System Prompt" },
  { value: "tools", label: "Tools" },
  { value: "json_mode", label: "JSON Mode" },
  { value: "structured_output", label: "Structured Output" },
  { value: "reasoning", label: "Reasoning" },
  { value: "web_search", label: "Web Search" },
  { value: "audio_in", label: "Audio Input" },
  { value: "audio_out", label: "Audio Output" },
  { value: "video_in", label: "Video Input" },
  { value: "video_out", label: "Video Output" },
  { value: "image_generation", label: "Image Generation" },
  { value: "document_input", label: "Document Input" },
  { value: "spreadsheet_input", label: "Spreadsheet Input" },
];

const getFilterMenus = (models: IModelExtended[]) => {
  const modelCategories = new Set(models.map((model) => model.model_category));
  const hasMultipleCategories = modelCategories.size > 1;

  // Check if any model has capabilities
  const hasCapabilities = models.some(
    (model) => model.capabilities && model.capabilities.length > 0
  );

  const allMenus = [
    {
      property: "provider",
      title: "Provider",
      options: getArrayFromProperty(models, "provider"),
    },
    {
      property: "model_category",
      title: "Category",
      options: getArrayFromProperty(models, "model_category"),
    },
    {
      property: "normalizedSpeed",
      title: "Speed",
      options: GAUGE_BUCKETS,
    },
    {
      property: "quality",
      title: "Quality",
      options: GAUGE_BUCKETS,
    },
    {
      property: "location",
      title: "Location",
      options: getArrayFromProperty(models, "location"),
    },
    {
      property: "capabilities",
      title: "Capabilities",
      options: CAPABILITY_OPTIONS,
    },
  ];

  return allMenus.filter((menu) => {
    if (menu.property === "model_category" && !hasMultipleCategories) {
      return false;
    }
    if (menu.property === "capabilities" && !hasCapabilities) {
      return false;
    }
    return true;
  });
};

const FilterMenu = ({
  menu,
  handleFilterChange,
  value = [],
  open,
  onOpen,
  onClose,
}: {
  menu: {
    title: string;
    options: any[];
    property: string;
  };
  handleFilterChange: (values: string[], property: string) => void;
  value?: string[];
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}) => {
  const handleCheckboxChange = (optionValue: string, checked: boolean) => {
    const newValues = checked
      ? [...value, optionValue]
      : value.filter((v) => v !== optionValue);
    handleFilterChange(newValues, menu.property);
  };

  return (
    <DropdownMenuSub open={open} onOpenChange={(o) => !o && onClose()}>
      <DropdownMenuSubTrigger
        aria-label={`${menu.title} menu button`}
        className="flex items-center justify-between gap-2 cursor-pointer"
        onPointerEnter={onOpen}
      >
        {menu.title}
      </DropdownMenuSubTrigger>

      <DropdownMenuSubContent
        sideOffset={8}
        alignOffset={-5}
        className="w-fit min-w-0 p-1 gap-1"
        onPointerLeave={onClose}
      >
        <div className="flex flex-col items-start gap-1">
          {menu.options.map((option, index) => (
            <label
              key={`checkbox-${option.property}-${index}`}
              className="flex items-center gap-2 px-2 h-8 cursor-pointer hover:bg-accent rounded-sm w-full"
            >
              <Checkbox
                className="bg-background"
                checked={value.includes(option.value)}
                onCheckedChange={(checked) =>
                  handleCheckboxChange(option.value, checked as boolean)
                }
              />
              {option.iconUrl ? (
                <img
                  src={option.iconUrl}
                  alt=""
                  className="size-4 shrink-0 object-contain"
                  loading="lazy"
                  decoding="async"
                  draggable={false}
                />
              ) : option.icon ? (
                <span className="flex items-center h-full shrink-0">
                  <option.icon fontSize="1rem" />
                </span>
              ) : null}
              <p className="text-sm">{option.label}</p>
            </label>
          ))}
        </div>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};

export const ModelPickerFilters = ({
  models,
  searchFilters,
  setSearchFilters,
  hasFiltersApplied,
  resetFilters,
}: {
  models: IModelExtended[];
  searchFilters: Record<string, string[]>;
  setSearchFilters: Dispatch<SetStateAction<Record<string, string[]>>>;
  hasFiltersApplied?: boolean;
  resetFilters: () => void;
}) => {
  const filterMenus = getFilterMenus(models);
  const [openSub, setOpenSub] = useState<string | null>(null);

  const activeFilterCount = useMemo(
    () =>
      Object.values(searchFilters).reduce(
        (count, values) => count + values.length,
        0
      ),
    [searchFilters]
  );

  const handleFilterChange = (values: string[], property: string) => {
    setSearchFilters((oldFilters) => {
      return {
        ...oldFilters,
        [property]: values,
      };
    });
  };

  return (
    // modal={false} — without this, the dropdown's own focus trap
    // fights the surrounding Dialog's trap and Radix's outside-click
    // detection never fires, so clicks outside the menu (but still
    // inside the Dialog) don't dismiss it.
    <DropdownMenu modal={false} onOpenChange={(o) => !o && setOpenSub(null)}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="default"
          aria-label="Filters menu button"
          className="hover:bg-background border-input focus-within:ring-ring focus-within:ring-1 focus-within:outline-none"
        >
          <ListFilter
            style={{ width: "1rem", height: "1rem", strokeWidth: "1.25px" }}
          />
          {activeFilterCount > 0
            ? `${activeFilterCount} ${activeFilterCount === 1 ? "Filter" : "Filters"}`
            : "Filter"}
          {activeFilterCount > 0 && (
            <span
              role="button"
              tabIndex={0}
              data-testid="model-filters-clear"
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                resetFilters();
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              className="inline-flex items-center justify-center h-4 w-4 opacity-50 hover:opacity-100 cursor-pointer"
            >
              <X size={12} color="var(--foreground)" />
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="end"
        className="w-fit p-1 gap-1"
      >
        {filterMenus.map((menu, index) => (
          <FilterMenu
            key={`model-filter-${index}`}
            value={searchFilters[menu.property]}
            menu={menu}
            handleFilterChange={handleFilterChange}
            open={openSub === menu.property}
            onOpen={() => setOpenSub(menu.property)}
            onClose={() => setOpenSub(null)}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
